/**
 * ChoiceRow — one choice in a list of them (a recipient to tick): the web
 * counterpart of the app's ChoiceRow (apps/mobile/src/components/common/ds/Sheet).
 * A flat row ruled off by a `borderSubtle` hairline, about 52px for two lines
 * and never under 44px: the `value` role for the label, an optional quiet
 * `aside` beside it on the same line (an address, cut short with an
 * ellipsis), and a `meta` line under it (wrapping, never cut). The whole row
 * is the tap target: a `<label>` around a native tick box (accent
 * `brandFill`), so the box, the words and the row all toggle it, and it reads
 * as one checkbox named by its words. Ticked rows take the `brandSubtle` wash.
 */
import React from 'react';
import { textRole, tokenVars } from '@turn-connect/ui';
import { ensureFieldCss } from './FormField';

export interface ChoiceRowProps {
  label: React.ReactNode;
  /** Quiet text after the label on its line (an address beside a name). */
  aside?: string;
  /** The line under the label: plain text or a node with toned parts. */
  meta?: React.ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  testId?: string;
  /** Read instead of the row's words (when the words alone are ambiguous). */
  ariaLabel?: string;
  /** Right of the words (a small action or icon). */
  right?: React.ReactNode;
}

export const ChoiceRow: React.FC<ChoiceRowProps> = ({ label, aside, meta, checked, onChange, disabled, testId, ariaLabel, right }) => {
  ensureFieldCss();
  return (
    <label style={{ ...rowStyle, ...(checked ? chosenStyle : null), cursor: disabled ? 'default' : 'pointer' }} data-testid={testId}>
      <input
        type="checkbox"
        className="tc-check"
        checked={checked}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={(e) => onChange(e.target.checked)}
        style={tickStyle}
      />
      <span style={wordsStyle}>
        <span style={lineStyle}>
          <span style={labelStyle}>{label}</span>
          {aside && <span style={asideStyle}>{aside}</span>}
        </span>
        {meta ? <span style={metaStyle}>{meta}</span> : null}
      </span>
      {right}
    </label>
  );
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  minHeight: 48,
  boxSizing: 'border-box',
  padding: '6px 12px',
  borderBottom: `1px solid ${tokenVars.borderSubtle}`,
  background: tokenVars.bg,
  minWidth: 0,
};
const chosenStyle: React.CSSProperties = { background: tokenVars.brandSubtle };
const tickStyle: React.CSSProperties = { width: 18, height: 18, margin: 0, flexShrink: 0, cursor: 'inherit' };
const wordsStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 };
const lineStyle: React.CSSProperties = { display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 };
const ellipsis: React.CSSProperties = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 };
// The label keeps its width first; the address beside it gives way.
const labelStyle: React.CSSProperties = { ...textRole('value'), ...ellipsis, flex: '0 1 auto' };
const asideStyle: React.CSSProperties = { ...textRole('meta'), ...ellipsis, flex: '1 1 0' };
// Wraps rather than cutting: a delivery problem at its end must stay readable.
const metaStyle: React.CSSProperties = { ...textRole('meta'), overflowWrap: 'anywhere' };

export default ChoiceRow;
